// Exchange modal brand marks. Generic glyphs come from the app icon layer
// (ui/icons/app — reicon); only the TON marks and the fiat flags stay local.

interface IconProps {
  size?: number
  className?: string
}

// The TON diamond, in the box "47 56 142 142". One path for the logo, the
// disc and the mark in the middle of the deposit QR (DepositQr.tsx).
export const TON_DIAMOND_BOX = { x: 47, y: 56, size: 142 } as const
export const TON_DIAMOND_PATH =
  'M74.101 62.197C57.68 62.197 47.268 79.912 55.531 94.235L109.964 188.582C113.619 194.922 122.781 194.922 126.436 188.582L180.88 94.235C189.132 79.934 178.72 62.197 162.31 62.197H74.101ZM162.288 78.841C166.031 78.841 168.234 82.812 166.45 85.907L137.856 137.091L137.851 137.099L126.506 159.046V78.841H162.288ZM109.872 78.852V159.024L98.538 137.088L98.533 137.08L69.929 85.921L69.847 85.772C68.213 82.7 70.405 78.852 74.09 78.852H109.872Z'

// Real TON blockchain logo (the diamond), cloned from Paper V0Y-0 — fill
// currentColor so it inherits the network-pill colour.
export function TonLogo({ size = 13, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="47 56 142 142"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path fillRule="evenodd" d={TON_DIAMOND_PATH} fill="currentColor" />
    </svg>
  )
}

// Toncoin currency mark — the official ton.org disc (#0098EA) with the white
// diamond. Reads at equal visual weight with the USDT disc and the GRAM gem
// in currency pills / pickers (the bare diamond glyph floated weightless).
export function TonMark({ size = 20, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 236 236"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <circle cx="118" cy="118" r="118" fill="#0098EA" />
      {/* Same canonical diamond path as TonLogo, optically centred in the disc. */}
      <path transform="translate(0 -9.5)" fillRule="evenodd" d={TON_DIAMOND_PATH} fill="#ffffff" />
    </svg>
  )
}

// Round EU flag (blue disc + 12-star ring, simplified to dots at chip size)
// for the Buy "EUR" option.
export function EuFlag({ size = 18 }: { size?: number }) {
  const stars = Array.from({ length: 12 }, (_, i) => {
    const a = (i / 12) * 2 * Math.PI
    return [12 + 6.6 * Math.sin(a), 12 - 6.6 * Math.cos(a)] as const
  })
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="12" fill="#003399" />
      {stars.map(([x, y]) => (
        <circle key={`${x.toFixed(2)}-${y.toFixed(2)}`} cx={x} cy={y} r="1.05" fill="#FFCC00" />
      ))}
      <circle cx="12" cy="12" r="11.5" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
    </svg>
  )
}

// Round US flag (stripes + canton, circle-clipped) for the Buy "USD" chip —
// круглый флаг, а не квадрат (фидбек).
export function UsFlag({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="us-round">
          <circle cx="12" cy="12" r="12" />
        </clipPath>
      </defs>
      <g clipPath="url(#us-round)">
        <rect width="24" height="24" fill="#FFFFFF" />
        <rect y="0" width="24" height="1.85" fill="#B22234" />
        <rect y="3.69" width="24" height="1.85" fill="#B22234" />
        <rect y="7.38" width="24" height="1.85" fill="#B22234" />
        <rect y="11.08" width="24" height="1.85" fill="#B22234" />
        <rect y="14.77" width="24" height="1.85" fill="#B22234" />
        <rect y="18.46" width="24" height="1.85" fill="#B22234" />
        <rect y="22.15" width="24" height="1.85" fill="#B22234" />
        <rect width="10.5" height="12.92" fill="#3C3B6E" />
      </g>
      <circle cx="12" cy="12" r="11.5" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
    </svg>
  )
}
